import { ContractSuretyType, type FileId, FileType, type SuretyBondFormType } from "@inrev/common";
import type { BondFormTemplate } from "@portal/api/bond-request/agent";
import type { FC } from "react";
import { type Control, useWatch } from "react-hook-form";
import type { BondFormData } from "../../../../types";
import { BOND_FORM_HEADER, BondFormSectionHeader } from "../../../form/BondFormSectionHeader";
import { FormBondFormSelect } from "../../../form/fields/FormBondFormSelect";
import { BondFormSelectInput } from "../../../form/inputs/BondFormSelectInput";
import { Checklist } from "../../../patterns/checklist";
import { Dropzone } from "../../../patterns/dropzone";
import { Icon, List } from "../../../primitives";
import { Tooltip } from "../../../ui/Tooltip";
import type { SubmissionFormData } from "../bondRequestSubmissionFormSchema";
import { type DocumentFile, DocumentFileRow } from "./DocumentFileRow";
import { StepFieldLabel } from "./StepFieldLabel";

type RequestedDocument = {
	key: string;
	label: string;
	types: FileType[];
	required?: boolean;
};

const BOND_REQUEST_FORM: RequestedDocument = {
	key: "bond-request-form",
	label: "Bond Request Form",
	types: [FileType.bond_request_form],
};

const CONTRACT: RequestedDocument = {
	key: "contract",
	label: "Contract",
	types: [FileType.contract, FileType.bid_package],
	required: true,
};

const RFP_BID_PACKAGE: RequestedDocument = {
	key: "contract",
	label: "RFP / Bid Package",
	types: [FileType.contract, FileType.bid_package],
	required: true,
};

const JOB_COST_BREAKDOWN: RequestedDocument = {
	key: "job-cost-breakdown",
	label: "Job Cost Breakdown",
	types: [FileType.job_cost_breakdown],
};

const BID_RESULTS: RequestedDocument = {
	key: "bid-results",
	label: "Bid Results",
	types: [FileType.bid_results],
};

const getRequestedDocuments = (
	contractSuretyType: ContractSuretyType | undefined,
): RequestedDocument[] => {
	switch (contractSuretyType) {
		case ContractSuretyType.bid:
			return [RFP_BID_PACKAGE, BOND_REQUEST_FORM];
		case ContractSuretyType.bid_to_final:
			return [CONTRACT, BID_RESULTS, BOND_REQUEST_FORM, JOB_COST_BREAKDOWN];
		case ContractSuretyType.final:
		default:
			return [CONTRACT, BOND_REQUEST_FORM, JOB_COST_BREAKDOWN, BID_RESULTS];
	}
};

const OTHER_DOCUMENTS_LABEL = "Other Documents";

const UPLOAD_GUIDANCE = `<p style="margin:0">No documents yet? Use <strong>Account Messages</strong> on the account page to discuss upcoming requests with your underwriter.</p>`;

const isBondFormSelected = (value: BondFormData | undefined) =>
	!!value?.templateId || (value?.upload.files.length ?? 0) > 0;

type BondFormFieldProps = {
	templates: BondFormTemplate[];
	allowedUploadTypes: Partial<Record<SuretyBondFormType, string>>;
};

/** RHF-bound host (e.g. the submission modal). Reads/writes the form's `bondForm` field. */
type RhfBondFormProps = BondFormFieldProps & {
	control: Control<SubmissionFormData>;
};

/** Plain controlled host (e.g. the create-request wizard, whose bond form lives in context). */
type ControlledBondFormProps = BondFormFieldProps & {
	value: BondFormData;
	onChange: (value: BondFormData) => void;
};

export type DocumentUploadSectionProps = {
	classifiedTypes: Set<FileType>;
	files: DocumentFile[];
	onDrop: (files: File[]) => void;
	onRemoveFile: (id: FileId) => void;
	uploadDisabled?: boolean;
	bondForm: RhfBondFormProps | ControlledBondFormProps;
	contractSuretyType: ContractSuretyType | undefined;
	trackingPrefix: string;
};

const BondFormHeader = ({ selected }: { selected: boolean }) => (
	<BondFormSectionHeader
		tooltipId="bond-form-guidance"
		showTooltip={!selected}
		label={
			<span className="flex items-center gap-1.5">
				<StepFieldLabel>{BOND_FORM_HEADER}</StepFieldLabel>
				{selected && (
					<Icon
						type="check-circle"
						filled
						color="confirm"
						size="mini"
						data-testid="bond-form-complete-indicator"
					/>
				)}
			</span>
		}
		className="gap-1.5"
		tooltipClassName="text-[16px]"
		tooltipMaxWidth={300}
		condensed={false}
	/>
);

const RhfBondFormField = ({ control, templates, allowedUploadTypes }: RhfBondFormProps) => {
	const value = useWatch({ control, name: "bondForm" });

	return (
		<div className="flex flex-col gap-3">
			<BondFormHeader selected={isBondFormSelected(value)} />
			<FormBondFormSelect<SubmissionFormData>
				name="bondForm"
				control={control}
				templates={templates}
				typeLabels={allowedUploadTypes}
				id="bondForm"
				compact
			/>
		</div>
	);
};

const ControlledBondFormField = ({
	value,
	onChange,
	templates,
	allowedUploadTypes,
}: ControlledBondFormProps) => (
	<div className="flex flex-col gap-3">
		<BondFormHeader selected={isBondFormSelected(value)} />
		<div className="relative">
			<BondFormSelectInput
				value={value}
				onChange={onChange}
				templates={templates}
				typeLabels={allowedUploadTypes}
				id="bondForm"
				compact
			/>
		</div>
	</div>
);

export const DocumentUploadSection: FC<DocumentUploadSectionProps> = ({
	classifiedTypes,
	files,
	onDrop,
	onRemoveFile,
	uploadDisabled,
	bondForm,
	contractSuretyType,
	trackingPrefix,
}) => {
	const requestedDocuments = getRequestedDocuments(contractSuretyType);
	const isComplete = (doc: RequestedDocument) =>
		doc.types.some((type) => classifiedTypes.has(type));

	return (
		<div className="flex flex-col gap-4" data-testid="document-upload-section">
			<div className="flex items-center gap-1.5">
				<StepFieldLabel>Document Upload</StepFieldLabel>
				<Tooltip
					id="document-upload-guidance"
					content={UPLOAD_GUIDANCE}
					className="text-[16px]"
					maxWidth={300}
					clickable
				/>
			</div>

			<div className="grid grid-cols-2 items-start gap-x-6">
				<Checklist data-testid="document-checklist" className="space-y-2">
					{requestedDocuments.map((doc) => {
						const complete = isComplete(doc);
						return (
							<Checklist.Item
								key={doc.key}
								data-testid={`document-checklist-item-${doc.key}`}
								data-complete={complete}
								state={complete ? "complete" : "incomplete"}
								className="text-[13.5px] text-secondary"
							>
								{doc.label}
								{doc.required && " *"}
							</Checklist.Item>
						);
					})}

					<List.Item
						data-testid="document-checklist-item-other"
						className="text-[13.5px] text-secondary"
						leading={<Icon type="plus-circle" size="mini" color="secondary" />}
					>
						{OTHER_DOCUMENTS_LABEL}
					</List.Item>
				</Checklist>

				<Dropzone
					testId="document-dropzone"
					onDrop={onDrop}
					disabled={uploadDisabled}
					multiple
					size="lg"
					trackingId={`${trackingPrefix}-doc-dropzone`}
				/>
			</div>

			{files.length > 0 && (
				<div className="flex flex-col gap-2" data-testid="files-step-file-list">
					{files.map((file) => (
						<DocumentFileRow
							key={file.id}
							file={file}
							onRemove={onRemoveFile}
							trackingId={`${trackingPrefix}-file-row-${file.id}`}
						/>
					))}
				</div>
			)}

			{"control" in bondForm ? (
				<RhfBondFormField {...bondForm} />
			) : (
				<ControlledBondFormField {...bondForm} />
			)}
		</div>
	);
};
